/**
 * plural.ts — множественное число (WEB-1874, этап 2).
 *
 * Русскому нужно три формы (1 воронка / 2 воронки / 5 воронок), английскому
 * две (1 funnel / 5 funnels). Без помощника получаются либо кривые фразы,
 * либо обходные «Воронок: 5», которые по-английски читаются заметно хуже.
 *
 * Правило берётся у `Intl.PluralRules`, а не пишется руками: рукописных копий
 * в кабинете уже было две (`utils/pluralRu.ts`, `utils/pluralizeRu.ts`) — обе
 * сперва свелись к этому помощнику тонкими обёртками (чтобы волна переноса не
 * лезла в чужие разделы), а по её окончании УДАЛЕНЫ: мест вызова не осталось
 * ни одного, и держать пустые обёртки значило бы предлагать следующему автору
 * третий способ склонять слова (ревизия пачки 15, R3-6). Копия правила в
 * продукте одна — здесь; у плеера своя, по языку ВОРОНКИ
 * (`utils/publicQuizStrings.ts`, сторож WEB-967).
 */
import type { UiLocale } from './locale';

/**
 * Формы по категориям CLDR. Русский заполняет one/few/many, английский —
 * one/other. Недостающая категория берётся по цепочке other → many → one:
 * дробное число в русском попадает в категорию `other`, и без цепочки строка
 * оказалась бы пустой.
 */
export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>>;

const INTL_TAG: Record<UiLocale, string> = { en: 'en-US', ru: 'ru-RU' };

const rulesCache = new Map<UiLocale, Intl.PluralRules>();

function rulesFor(locale: UiLocale): Intl.PluralRules {
  const cached = rulesCache.get(locale);
  if (cached) return cached;
  const rules = new Intl.PluralRules(INTL_TAG[locale]);
  rulesCache.set(locale, rules);
  return rules;
}

/** Форма слова для числа `n` на языке `locale`. */
export function plural(
  locale: UiLocale,
  n: number,
  forms: PluralForms,
): string {
  const category = rulesFor(locale).select(n);
  return (
    forms[category] ?? forms.other ?? forms.many ?? forms.one ?? ''
  );
}
